import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrash2, FiPlus, FiMinus, FiCreditCard, FiShoppingCart } = FiIcons;

function Cart() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateCartQuantity, createOrder } = useApp();

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = () => {
    if (cart.length === 0) return;

    const orderData = {
      prisonId: cart[0].prisonId,
      customerName: 'John Doe', // This would come from the visiting details
      status: 'pending'
    };

    const order = createOrder(orderData);
    navigate('/order-confirmation', { state: { order } });
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-government-800 rounded-xl shadow-lg p-8 text-center transition-colors duration-300"
        >
          <SafeIcon icon={FiShoppingCart} className="w-16 h-16 text-government-300 dark:text-government-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-government-900 dark:text-white mb-2">
            Your cart is empty
          </h2>
          <p className="text-government-600 dark:text-government-300 mb-6">
            Add items from the menu to get started
          </p>
          <button
            onClick={() => navigate('/customer/menu')}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Browse Menu
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-government-800 rounded-xl shadow-lg p-8 transition-colors duration-300"
      >
        <h1 className="text-3xl font-bold text-government-900 dark:text-white mb-8">
          Your Order
        </h1>

        <div className="space-y-4 mb-8">
          {cart.map((item) => (
            <motion.div
              key={item.cartId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4 p-4 border border-government-200 dark:border-government-600 rounded-lg transition-colors duration-300"
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-government-900 dark:text-white">
                  {item.name}
                </h3>
                <p className="text-sm text-government-600 dark:text-government-300">
                  {item.category}
                </p>
                <p className="text-lg font-bold text-primary-500">
                  £{item.price.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => updateCartQuantity(item.cartId, item.quantity - 1)}
                  className="p-1 text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiMinus} className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium text-government-900 dark:text-white">
                  {item.quantity}
                </span>
                <button
                  onClick={() => updateCartQuantity(item.cartId, item.quantity + 1)}
                  className="p-1 text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                </button>
              </div>
              <div className="text-right">
                <p className="font-bold text-government-900 dark:text-white">
                  £{(item.price * item.quantity).toFixed(2)}
                </p>
                <button
                  onClick={() => removeFromCart(item.cartId)}
                  className="text-red-600 hover:text-red-800 transition-colors mt-1"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="border-t border-government-200 dark:border-government-600 pt-6 transition-colors duration-300">
          <div className="flex justify-between items-center mb-6">
            <span className="text-xl font-semibold text-government-900 dark:text-white">
              Total:
            </span>
            <span className="text-2xl font-bold text-primary-500">
              £{total.toFixed(2)}
            </span>
          </div>

          <div className="bg-government-50 dark:bg-government-700 p-4 rounded-lg mb-6 transition-colors duration-300">
            <p className="text-sm text-government-600 dark:text-government-300">
              <strong>Payment Mode:</strong> This is a demo version. In the live system, 
              secure payment processing would be integrated.
            </p>
          </div>

          <button
            onClick={handleCheckout}
            className="w-full bg-primary-500 hover:bg-primary-600 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiCreditCard} className="w-5 h-5" />
            <span>Place Order (Demo)</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default Cart;